import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Capacitor } from '@capacitor/core';
import Home from './pages/Home';
import AndroidBackButtonHandler from './components/common/AndroidBackButtonHandler';
import Auth from './pages/Auth';
import AboutPage from './pages/About';
import CampaignDetail from './pages/CampaignDetail';
import DeleteAccount from './pages/DeleteAccount';
import Header from './components/layout/Header';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './hooks/useAuth';
import Profile from './pages/Profile';
import './styles/index.css';
import { Toaster } from 'react-hot-toast';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Contribute from './pages/Contribute';
import ProtectedRoute from './routes/ProtectedRoute';
import ContributionDetails from './pages/ContributionDetails';
import ResetPassword from './pages/ResetPassword';
import ScrollToTop from './components/common/ScrollToTop';
import UpdatePassword from './pages/UpdatePassword';
import MobileWelcome from './pages/MobileWelcome';
import { ANALYTICS_EVENTS, trackEvent, trackPageView } from './utils/analytics';
import SocialLinks from './components/common/SocialLinks';

const MOBILE_WELCOME_EXCLUDED_PATHS = new Set([
  '/mobile-welcome',
  '/auth',
  '/reset-password',
  '/update-password',
  '/delete-account',
]);

const MOBILE_EXPLORE_SESSION_KEY = 'ballotr_mobile_explore_session';

function AppRoutes() {
  const location = useLocation();
  const { user, loading, isPasswordRecovery } = useAuth();

  const isNativeApp = Capacitor.isNativePlatform();

  const isPasswordRoute =
    location.pathname === '/reset-password' ||
    location.pathname === '/update-password';

  const shouldHideAppChrome = isPasswordRoute || isPasswordRecovery;

  const hasMobileExploreSession = (() => {
    try {
      return sessionStorage.getItem(MOBILE_EXPLORE_SESSION_KEY) === 'true';
    } catch {
      return false;
    }
  })();

  const shouldShowMobileWelcome =
    isNativeApp &&
    !loading &&
    !user &&
    !isPasswordRecovery &&
    !hasMobileExploreSession &&
    !MOBILE_WELCOME_EXCLUDED_PATHS.has(location.pathname);

  useEffect(() => {
    trackPageView(location.pathname, {
      path: location.pathname,
      search: location.search,
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname === '/mobile-welcome') {
      trackEvent(ANALYTICS_EVENTS.MOBILE_WELCOME_SEEN, {
        platform: isNativeApp ? 'native' : 'web',
      });
    }
  }, [isNativeApp, location.pathname]);

  useEffect(() => {
    if (!user) {
      return;
    }

    try {
      sessionStorage.removeItem(MOBILE_EXPLORE_SESSION_KEY);
    } catch {
      // Ignore storage failures.
    }
  }, [user]);

  if (loading) {
    return (
      <>
        <Toaster position="top-center" />
        <div className="flex min-h-screen items-center justify-center bg-[#034C8C] text-white">
          <div className="text-lg font-semibold">Loading Ballotr...</div>
        </div>
      </>
    );
  }

  if (
    isPasswordRecovery &&
    location.pathname !== '/update-password'
  ) {
    return <Navigate to="/update-password" replace />;
  }

  if (user && location.pathname === '/mobile-welcome') {
    return <Navigate to="/" replace />;
  }

  if (shouldShowMobileWelcome) {
    return <Navigate to="/mobile-welcome" replace />;
  }

  if (location.pathname === '/mobile-welcome') {
    return (
      <>
        <Toaster position="top-center" />
        <Routes>
          <Route path="/mobile-welcome" element={<MobileWelcome />} />
          <Route path="*" element={<Navigate to="/mobile-welcome" replace />} />
        </Routes>
      </>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      {!shouldHideAppChrome && <Header />}
      <Toaster position="top-center" />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/advertise" element={<Navigate to="/about#advertise" replace />} />
          <Route path="/campaign/:id" element={<CampaignDetail />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contribute"
            element={
              <ProtectedRoute>
                <Contribute />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contribute/details"
            element={
              <ProtectedRoute>
                <ContributionDetails />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {!shouldHideAppChrome && (
        <footer className="border-t border-gray-200 bg-white px-4 py-8">
          <div className="max-w-container mx-auto">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-gray-500 mb-4">
                <a href="/about#advertise" className="hover:text-primary transition">
                  Advertise
                </a>
            
                <Link to="/terms" className="hover:text-primary transition">
                  Terms
                </Link>
            
              <Link to="/privacy" className="hover:text-primary transition">
                Privacy
                </Link>
            
              <Link
                  to="/about#official-sources"
                  className="hover:text-primary transition"
              >
                  Government Disclaimer &amp; Sources
                </Link>
              </div>
          
            <p className="mx-auto mb-6 max-w-3xl text-center text-xs leading-5 text-gray-500">
              Ballotr is an independent civic engagement platform and does not
              represent any federal, state, or local government entity.
               </p>

            <div className="flex justify-center">
              <SocialLinks variant="footer" className="text-center" />
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <AndroidBackButtonHandler />
          <ScrollToTop />
          <AppRoutes />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;